import {
  createFileRoute,
  getRouteApi,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ConfirmModal } from "@/components/ConfirmModal.tsx";
import { CopyButton } from "@/components/CopyButton.tsx";
import { ChevronRight, EyeOff, Lock, Trash2 } from "lucide-react";
import { UploadYourFirstFileSection } from "@/components/UploadYourFirstFileSection.tsx";
import { GetStartedDrawer } from "@/components/GetStartedDrawer.tsx";
import { ApiError, UnauthorizedError, apiDelete, apiGet } from "@/lib/api.ts";
import { runWithConcurrency } from "@/lib/concurrency.ts";
import { setCachedHasFiles } from "@/lib/fileCount.ts";
import { humanCount, relativeTime } from "@/lib/format.ts";
import { fetchVisitCount, getCachedVisitCount } from "@/lib/visitCount.ts";

type FileItem = {
  identifier: string;
  slug: string;
  nanoid: string;
  title: string | null;
  scope: "org" | "private";
  urlAccess: "public" | "password";
  version: number;
  hostedUrl: string;
  rawUrl: string;
  versionedHostedUrl: string;
  versionedRawUrl: string;
  createdAt: string;
  updatedAt: string;
};

type FilesResponse = {
  items: FileItem[];
  nextCursor: string | null;
};

type DeleteError = { identifier: string; message: string };

const BULK_DELETE_CONCURRENCY = 4;
const VISIT_COUNT_CONCURRENCY = 4;

async function fetchFiles(cursor?: string): Promise<FilesResponse> {
  const qs = new URLSearchParams({ limit: "20" });
  if (cursor) qs.set("cursor", cursor);
  return apiGet<FilesResponse>(`/api/v1/files?${qs.toString()}`);
}

// FRE-293: pull `me` + `onboarding` from the root loader so the empty-state
// card has the org list + drawer trigger without an extra fetch. `getRouteApi`
// is the typed accessor in TanStack Router for cross-route loader data.
const rootRoute = getRouteApi("__root__");

export const Route = createFileRoute("/")({
  beforeLoad: async () => {
    // Cheap auth gate: hit /me before rendering. On 401, bounce to /login.
    // FRE-289: on 400 no_active_org bounce to /orgs/new (fresh signups never
    // hit this path — the worker hook creates a default org — but pre-existing
    // zero-org accounts and hook-failure cases land here).
    try {
      await apiGet<{ userId: string }>("/api/v1/me");
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({ to: "/login", search: { next: "/" } });
      }
      if (err instanceof ApiError && err.status === 400 && err.message.includes("no_active_org")) {
        throw redirect({ to: "/orgs/new" });
      }
      throw err;
    }
  },
  component: FilesListComponent,
});

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0">
      <div className="h-4 w-4 rounded bg-muted animate-pulse" />
      <div className="h-4 w-4 rounded bg-muted animate-pulse" />
      <div className="flex-1 h-4 rounded bg-muted animate-pulse" />
      <div className="h-4 w-8 rounded bg-muted animate-pulse" />
      <div className="h-4 w-16 rounded bg-muted animate-pulse" />
      <div className="h-4 w-16 rounded bg-muted animate-pulse" />
      <div className="h-6 w-20 rounded bg-muted animate-pulse" />
    </div>
  );
}

// Tri-state header checkbox: indeterminate when 0 < selected < total.
// React doesn't expose `indeterminate` as a prop on <input>, so we apply it
// via ref. Kept out of the main component to avoid one more useEffect inline.
function HeaderCheckbox({
  state,
  onChange,
  disabled,
}: {
  state: "none" | "some" | "all";
  onChange: () => void;
  disabled: boolean;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === "all"}
      onChange={onChange}
      disabled={disabled}
      aria-label="Select all files"
      className="h-4 w-4 cursor-pointer rounded border-input text-foreground focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}

function FilesListComponent() {
  const navigate = useNavigate();
  const router = useRouter();
  // FRE-293: lift `me` + `onboarding` off the root loader. Root's loader
  // returns `me: null` only when /api/v1/me 401'd, but `/`'s beforeLoad
  // already redirects to /login in that case — so by the time we render
  // here `me` is effectively non-null. We re-assert it as a guard so
  // TypeScript doesn't have to thread the redirect's control flow.
  const loaderData = rootRoute.useLoaderData();
  const me = loaderData.me;
  const onboarding = loaderData.onboarding;
  const [items, setItems] = useState<FileItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Visit-count map: identifier → number (loaded) | "error" (giving up) |
  // undefined (loading or not yet queued). Single-source-of-truth lives in the
  // module-level cache in visitCount.ts; this is just the reactive mirror so
  // rows re-render when the orchestrator below resolves each fetch.
  const [visitCounts, setVisitCounts] = useState<Map<string, number | "error">>(new Map());

  // Bulk-delete state. `selected` survives "Load more" — only cleared after a
  // successful bulk delete or an explicit Clear / refresh.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteTotal, setDeleteTotal] = useState(0);
  const [deleteErrors, setDeleteErrors] = useState<DeleteError[]>([]);

  const loadFirstPage = useCallback(async () => {
    setInitialLoading(true);
    setError(null);
    try {
      const data = await fetchFiles();
      setItems(data.items);
      setCursor(data.nextCursor);
      // FRE-293: seed the file-count cache so the drawer's section 1
      // auto-collapses immediately after upload without a redundant probe.
      // Cache is keyed by active org id — matches the active-org context the
      // worker resolves this list against. Guard for the unlikely null-`me`
      // path: `/`'s beforeLoad already redirects on 401, so this is purely
      // belt-and-braces during the redirect tick.
      if (me) {
        setCachedHasFiles(me.organizationId, data.items.length > 0);
      }
      // Refresh wipes any prior selection — the underlying identifiers may
      // have shifted and stale selections are confusing.
      setSelected(new Set());
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await navigate({ to: "/login", search: { next: "/" } });
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load files";
      setError(message);
    } finally {
      setInitialLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  // Visit-count orchestrator: after each items change (initial load + Load
  // more) seed the reactive map from the module cache for any identifiers
  // that already have a fresh entry (the common case on back-navigation),
  // then queue fetches for the rest at concurrency 4. Cancellation flag
  // protects against state updates after the items list shifts again
  // (e.g. refresh during a long fetch). Errors per row degrade to "—"; we
  // never surface a banner because visit counts are decorative.
  useEffect(() => {
    if (items.length === 0) return;
    let cancelled = false;

    setVisitCounts((prev) => {
      let mutated = false;
      const next = new Map(prev);
      for (const it of items) {
        if (next.has(it.identifier)) continue;
        const cached = getCachedVisitCount(it.identifier);
        if (cached !== null) {
          next.set(it.identifier, cached);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });

    const toFetch = items
      .map((it) => it.identifier)
      .filter((id) => getCachedVisitCount(id) === null);

    if (toFetch.length === 0) return;

    void runWithConcurrency(toFetch, VISIT_COUNT_CONCURRENCY, async (identifier) => {
      try {
        const count = await fetchVisitCount(identifier);
        if (cancelled) return;
        setVisitCounts((prev) => {
          if (prev.get(identifier) === count) return prev;
          const next = new Map(prev);
          next.set(identifier, count);
          return next;
        });
      } catch {
        if (cancelled) return;
        setVisitCounts((prev) => {
          if (prev.get(identifier) === "error") return prev;
          const next = new Map(prev);
          next.set(identifier, "error");
          return next;
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [items]);

  async function handleLoadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const data = await fetchFiles(cursor);
      setItems((prev) => [...prev, ...data.items]);
      setCursor(data.nextCursor);
      // Newly loaded rows stay unselected — `selected` is preserved as-is.
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await navigate({ to: "/login", search: { next: "/" } });
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load more";
      setError(message);
    } finally {
      setLoadingMore(false);
    }
  }

  function handleRowClick(item: FileItem) {
    void navigate({ to: "/files/$identifier", params: { identifier: item.identifier } });
  }

  function handleRowKey(event: React.KeyboardEvent, item: FileItem) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRowClick(item);
    }
  }

  function toggleRow(identifier: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(identifier)) next.delete(identifier);
      else next.add(identifier);
      return next;
    });
  }

  const headerState: "none" | "some" | "all" = useMemo(() => {
    if (items.length === 0 || selected.size === 0) return "none";
    if (selected.size >= items.length && items.every((it) => selected.has(it.identifier)))
      return "all";
    return "some";
  }, [items, selected]);

  function toggleSelectAll() {
    setSelected((prev) => {
      if (headerState === "all") return new Set();
      const next = new Set(prev);
      for (const it of items) next.add(it.identifier);
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function openConfirm() {
    if (selected.size === 0 || deleting) return;
    setDeleteErrors([]);
    setConfirmOpen(true);
  }

  async function runBulkDelete() {
    // Snapshot the identifiers so concurrent state updates can't shift the
    // working set mid-flight.
    const ids = Array.from(selected);
    setConfirmOpen(false);
    setDeleting(true);
    setDeleteProgress(0);
    setDeleteTotal(ids.length);
    setDeleteErrors([]);

    const errors: DeleteError[] = [];
    let done = 0;

    await runWithConcurrency(ids, BULK_DELETE_CONCURRENCY, async (identifier) => {
      try {
        await apiDelete(`/api/v1/files/${encodeURIComponent(identifier)}`);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          // Don't navigate mid-loop — we'll surface it as an error per item
          // and let the post-flight refresh redirect if still unauthed.
          errors.push({ identifier, message: "Unauthorized" });
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Delete failed";
        errors.push({ identifier, message });
      } finally {
        done += 1;
        setDeleteProgress(done);
      }
    });

    setDeleteErrors(errors);
    setDeleting(false);
    // Clear selection regardless — partial failures stay surfaced via the
    // error banner, but the action bar shouldn't keep prompting deletes on
    // identifiers that may already be gone.
    setSelected(new Set());
    await loadFirstPage();
  }

  // Sign-out moved into the root header (FRE-272). Keep router reference for
  // future invalidate calls (e.g. post-bulk-delete) without lint noise.
  void router;

  const selectedCount = selected.size;
  const confirmIds = useMemo(() => Array.from(selected), [selected]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Files</h1>
      </div>

      {error ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void loadFirstPage()}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {deleteErrors.length > 0 ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium">
              {deleteErrors.length} file{deleteErrors.length === 1 ? "" : "s"} failed to delete
            </span>
            <button
              type="button"
              onClick={() => {
                setDeleteErrors([]);
                void loadFirstPage();
              }}
              className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
            >
              Refresh
            </button>
          </div>
          <ul className="mt-2 list-disc pl-5 text-xs text-destructive">
            {deleteErrors.slice(0, 3).map((e) => (
              <li key={e.identifier} className="font-mono">
                <span className="font-semibold">{e.identifier}</span>: {e.message}
              </li>
            ))}
            {deleteErrors.length > 3 ? (
              <li className="font-sans italic">…and {deleteErrors.length - 3} more</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {selectedCount > 0 || deleting ? (
        <div className="sticky top-0 z-10 mb-3 flex items-center gap-3 rounded border border-amber-200 bg-amber-50 p-3">
          {deleting ? (
            <span className="text-sm text-amber-900">
              Deleting {deleteProgress} of {deleteTotal}…
            </span>
          ) : (
            <>
              <span className="text-sm text-amber-900">
                {selectedCount} file{selectedCount === 1 ? "" : "s"} selected
              </span>
              <span aria-hidden="true" className="text-amber-300">
                •
              </span>
              <button
                type="button"
                onClick={openConfirm}
                className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-destructive focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1"
              >
                <Trash2 className="size-4" />
                Delete {selectedCount} file{selectedCount === 1 ? "" : "s"}
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="rounded-md border border-amber-300 bg-card px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Clear
              </button>
            </>
          )}
        </div>
      ) : null}

      {!initialLoading && items.length === 0 && !error && me ? (
        // FRE-293: empty-state card renders section 1 inline ("Upload your
        // first file"). The "Connect a client" pointer below it opens the same
        // drawer the header uses. Once the user uploads, items.length > 0 and
        // the standard table takes over. The `me` check is belt-and-braces
        // (beforeLoad redirects on 401, so `me` is effectively non-null
        // by the time we render here).
        <>
          <UploadYourFirstFileSection
            organizations={me.organizations}
            defaultOrganizationId={me.organizationId}
            host={typeof window !== "undefined" ? window.location.origin : ""}
            variant="inline-card"
          />
          <p className="mt-3 text-sm text-muted-foreground">
            Want to use Claude, Cursor, or VS Code?{" "}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="font-medium text-primary hover:underline"
            >
              Open the Get started drawer
            </button>{" "}
            → Connect a client
          </p>
          <GetStartedDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            onboarding={onboarding}
            organizations={me.organizations}
            activeOrganizationId={me.organizationId}
          />
        </>
      ) : (
        <div className="rounded-md border border-border bg-card overflow-hidden">
          {initialLoading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : items.length === 0 ? (
            // Files-error path: keep the legacy placeholder until the error
            // banner above unblocks the empty-state card render. FRE-293
            // intentionally keeps this fallback so the empty-card UI only
            // shows when we have a clean files=0 read.
            <div className="px-6 py-12 text-center text-sm text-muted-foreground">
              No files yet.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <div className="shrink-0">
                  <HeaderCheckbox
                    state={headerState}
                    onChange={toggleSelectAll}
                    disabled={deleting}
                  />
                </div>
                <div className="w-10 shrink-0" aria-hidden="true" />
                <div className="flex-1">Name</div>
              </div>
              {items.map((item) => {
                const label = item.title ?? item.identifier;
                const sub = item.title ? item.identifier : null;
                const isSelected = selected.has(item.identifier);
                return (
                  <div
                    key={item.identifier}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowClick(item)}
                    onKeyDown={(e) => handleRowKey(e, item)}
                    aria-selected={isSelected}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 cursor-pointer focus:outline-none ${
                      isSelected
                        ? "bg-amber-50 hover:bg-amber-100 focus:bg-amber-100"
                        : "hover:bg-muted/40 focus:bg-muted/40"
                    }`}
                  >
                    {/* Checkbox must NOT bubble to the row (which navigates). */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="shrink-0"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(item.identifier)}
                        disabled={deleting}
                        aria-label={`Select ${item.identifier}`}
                        className="h-4 w-4 cursor-pointer rounded border-input text-foreground focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground w-10 shrink-0">
                      {item.urlAccess !== "public" ? <Lock className="size-4" /> : null}
                      {item.scope === "private" ? <EyeOff className="size-4" /> : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{label}</div>
                      {sub ? (
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {sub}
                        </div>
                      ) : null}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground w-10 text-right shrink-0">
                      v{item.version}
                    </div>
                    <div
                      className="text-xs text-muted-foreground w-20 text-right shrink-0 tabular-nums"
                      title={
                        typeof visitCounts.get(item.identifier) === "number"
                          ? `${visitCounts.get(item.identifier)} lifetime visits`
                          : undefined
                      }
                    >
                      {(() => {
                        const v = visitCounts.get(item.identifier);
                        if (typeof v === "number") {
                          return `${humanCount(v)} visit${v === 1 ? "" : "s"}`;
                        }
                        return "—";
                      })()}
                    </div>
                    <div className="text-xs text-muted-foreground w-24 text-right shrink-0">
                      {relativeTime(item.updatedAt)}
                    </div>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <CopyButton
                        value={item.hostedUrl}
                        label="Copy URL"
                        stopPropagation
                        onError={setError}
                      />
                    </div>
                    <span className="text-muted-foreground/60 shrink-0" aria-hidden="true">
                      <ChevronRight className="size-4" />
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {cursor ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => void handleLoadMore()}
            disabled={loadingMore}
            className="rounded-md border border-input px-3 py-1.5 text-sm font-medium text-foreground/80 hover:bg-muted disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        title={`Delete ${selectedCount} file${selectedCount === 1 ? "" : "s"}?`}
        body={
          confirmIds.length <= 5 ? (
            <>
              <p>This action can't be undone. The following will be deleted:</p>
              <ul className="mt-2 list-disc pl-5 font-mono text-xs text-foreground/80">
                {confirmIds.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p>This action can't be undone. The following will be deleted:</p>
              <ul className="mt-2 list-disc pl-5 font-mono text-xs text-foreground/80">
                {confirmIds.slice(0, 3).map((id) => (
                  <li key={id}>{id}</li>
                ))}
                <li className="font-sans italic">…and {confirmIds.length - 3} more</li>
              </ul>
            </>
          )
        }
        confirmLabel={`Delete ${selectedCount} file${selectedCount === 1 ? "" : "s"}`}
        danger
        busy={deleting}
        onConfirm={() => void runBulkDelete()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
